import { getTagColors } from "@taskfortress/core/src/utils/colors"
import React, { useMemo, useCallback, useState } from "react"
import { useData } from "../../providers/DataProvider"
import { Box } from "../Box"
import { Text } from "../Text"
import { FormControl } from "../gluestack/form-control"
import { TagChip } from "../tags/TagChip"
import { FieldError } from "./FieldError"
import { FieldLabel } from "./FieldLabel"
import {
  SearchFilterField,
  type SearchFilterFieldItem,
} from "./SearchFilterField"

// TagField component
export type TagFieldProps = {
  value?: string[]
  handleChange: (tags: string[]) => void
  suggestions: string[]
  label?: string
  name: string
  required?: boolean
  className?: string
  placeholder?: string
  disabled?: boolean
  hideError?: boolean
  error?: string
}

export const TagField: React.FC<TagFieldProps> = ({
  value,
  handleChange,
  suggestions,
  label,
  required,
  className,
  placeholder = "Add tags...",
  disabled,
  hideError,
  error,
  name,
}) => {
  const [inputValue, setInputValue] = useState("")
  const { tags } = useData()

  const safeTagValues = useMemo(() => value || [], [value])

  const filteredSuggestions = useMemo(
    () =>
      suggestions.filter(
        (suggestion) =>
          suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
          !safeTagValues.includes(suggestion),
      ),
    [suggestions, inputValue, safeTagValues],
  )

  const suggestionItems = useMemo(
    (): SearchFilterFieldItem[] =>
      filteredSuggestions.map((suggestion) => ({
        id: suggestion,
        data: suggestion,
      })),
    [filteredSuggestions],
  )

  const handleAddTag = useCallback(
    (tag: string) => {
      const trimmedTag = tag.trim()
      if (trimmedTag && !safeTagValues.includes(trimmedTag)) {
        handleChange([...safeTagValues, trimmedTag])
      }
      setInputValue("")
    },
    [safeTagValues, handleChange],
  )

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      handleChange(safeTagValues.filter((tag) => tag !== tagToRemove))
    },
    [safeTagValues, handleChange],
  )

  const handleSuggestionSelect = useCallback(
    (item: SearchFilterFieldItem) => {
      handleAddTag(item.data)
    },
    [handleAddTag],
  )

  const handleInputSubmit = useCallback(() => {
    if (inputValue.trim()) {
      handleAddTag(inputValue)
    }
  }, [inputValue, handleAddTag])

  const renderSuggestionItem = useCallback(
    (item: SearchFilterFieldItem, _index: number, _isSelected: boolean) => {
      const suggestion = item.data

      // Find the tag in stored tags first
      const storedTag = tags.find((tag) => tag.name === suggestion)
      const backgroundColor =
        storedTag?.color || getTagColors(suggestion).backgroundColor

      return (
        <Box className="flex-row items-center p-3">
          <Box
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor }}
          />
          <Text className="flex-1">{suggestion}</Text>
        </Box>
      )
    },
    [tags],
  )

  const tagChips = useMemo(
    () =>
      safeTagValues.map((tag) => (
        <TagChip
          key={tag}
          tagName={tag}
          onPress={() => handleRemoveTag(tag)}
          className="mr-1 mb-1"
        />
      )),
    [safeTagValues, handleRemoveTag],
  )

  return (
    <FormControl
      className={className}
      isInvalid={!!error}
      isRequired={required}
      isDisabled={disabled}
    >
      <FieldLabel label={label} required={required} />

      {/* Search Filter Field for adding tags */}
      <SearchFilterField
        items={suggestionItems}
        renderItem={renderSuggestionItem}
        onItemSelect={handleSuggestionSelect}
        searchValue={inputValue}
        onSearchChange={setInputValue}
        onEnterPress={handleInputSubmit}
        placeholder={placeholder}
        name={`${name}_input`}
        hideError
        showSearchIcon={true}
      >
        {/* Selected tags display */}
        {safeTagValues.length > 0 && (
          <Box className="flex-row flex-wrap pt-2">{tagChips}</Box>
        )}
      </SearchFilterField>

      {!hideError && <FieldError error={error} />}
    </FormControl>
  )
}
